import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins, X } from 'lucide-react';
import { useCredits } from '../contexts/CreditsContext';
import CreditHistory from './CreditHistory';
import './Modal.css';
import './CreditBadge.css';

const LOW_THRESHOLD = 50;

function CreditBadge({ collapsed }) {
  const { credits } = useCredits();
  const [open, setOpen] = useState(false);

  // Escape closes the pop-up; listener exists only while it is open
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!credits) return null;               // hide on error rather than show a wrong 0

  const total = credits.total;
  const color = total === 0 ? '#b91c1c' : total < LOW_THRESHOLD ? '#b45309' : '#047857';
  const bg = total === 0 ? '#fef2f2' : total < LOW_THRESHOLD ? '#fffbeb' : '#ecfdf5';
  const level = total === 0 ? 'empty' : total < LOW_THRESHOLD ? 'low' : 'ok';

  return (
    <>
      <button
        type="button"
        className="credit-badge"
        onClick={() => setOpen(true)}
        title="Credits — click for details"
        aria-label={`Credits: ${total.toLocaleString()} remaining. Open details`}
        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.5rem 0.75rem',
                 margin: '0 0 0.5rem', border: `1px solid ${color}33`, borderRadius: '8px', background: bg,
                 color, cursor: 'pointer', fontWeight: 600 }}
      >
        <Coins size={16} />
        {!collapsed && <span className="credit-badge-text">{total.toLocaleString()} credits</span>}
      </button>

      {open && (
        <div className="ui-dialog-overlay" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
               role="dialog" aria-modal="true" aria-label="Your credits"
               className="ui-dialog ui-dialog--credits">
            <div className="ui-dialog-head">
              <h2 className="ui-dialog-title">Your credits</h2>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close"
                      className="ui-dialog-close"><X size={18} /></button>
            </div>
            <div className="ui-dialog-body">
              <p className="credit-pop-summary">
                <strong className={`credit-pop-total credit-pop-total--${level}`}>{total.toLocaleString()}</strong> credits
                {' '}({credits.monthly.toLocaleString()} monthly · {credits.topup.toLocaleString()} top-up).
                Monthly credits are used first and reset when your plan renews; top-up credits never expire.
              </p>
              <p className="credit-pop-note">
                1 credit per new prospect found by a search, and 1 credit per email checked.{' '}
                <Link to="/billing#credits" onClick={() => setOpen(false)}>Buy more credits on the Billing page.</Link>
              </p>
              <h3 className="credit-pop-subhead">Recent activity</h3>
              <CreditHistory />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CreditBadge;
